import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactTooltip from "react-tooltip";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";

import FloatingPanel from "../../common/components/FloatingPanel";
import { CkeditorConfiguration } from "../../smartCharts/CkEditorConfig";
import { stripExecutiveSummaryHtml } from "./executiveSummaryUtils";
import { SAMPLE_TEXT_VIEWER_DEFAULT_SIZE } from "./executiveSummarySampleTextViewerLayout";
import "./ExecutiveSummarySampleTextViewer.css";

const SAMPLE_TEXT_VIEWER_READONLY_ID = "exec-sum-sample-text-viewer";
const SAMPLE_TEXT_VIEWER_MIN_SIZE = { width: 320, height: 240 };
const SAMPLE_TEXT_VIEWER_MAX_SIZE = { width: 900, height: 720 };
const EXEC_SUM_SAMPLE_TEXT_COPY_TIP = "Copy sample text";

async function copyHtmlToClipboard(html, plainText) {
    if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([
            new ClipboardItem({
                "text/html": new Blob([html], { type: "text/html" }),
                "text/plain": new Blob([plainText], { type: "text/plain" })
            })
        ]);
        return;
    }

    const container = document.createElement("div");
    container.innerHTML = html;
    container.setAttribute("contenteditable", "true");
    container.style.position = "fixed";
    container.style.left = "-9999px";
    document.body.appendChild(container);

    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(container);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand("copy");
    selection.removeAllRanges();
    document.body.removeChild(container);
}

export default function ExecutiveSummarySampleTextViewer({
    label = "",
    sampleText = "",
    editorKey = "sample-text",
    defaultSize = SAMPLE_TEXT_VIEWER_DEFAULT_SIZE,
    initialPosition = null,
    onClose
}) {
    const [data, setData] = useState(sampleText ?? "");
    const [copied, setCopied] = useState(false);
    const editorRef = useRef(null);
    const copyTooltipId = useMemo(() => `exec-sum-sample-copy-${editorKey}`, [editorKey]);
    const hasContent = stripExecutiveSummaryHtml(sampleText ?? "").length > 0;

    useEffect(() => {
        setData(sampleText ?? "");
    }, [sampleText, editorKey]);

    useEffect(() => {
        setCopied(false);
        editorRef.current = null;
    }, [sampleText, editorKey]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [copied, hasContent]);

    const readOnlyEditorConfig = useMemo(
        () => ({
            ...CkeditorConfiguration,
            toolbar: []
        }),
        []
    );

    const handleCopy = useCallback(
        async e => {
            e.stopPropagation();
            if (!hasContent) return;
            const html = editorRef.current?.getData?.() || sampleText || "";
            const plainText = stripExecutiveSummaryHtml(html);

            try {
                const editor = editorRef.current;
                if (editor) {
                    editor.model.change(writer => {
                        writer.setSelection(editor.model.document.getRoot(), "in");
                    });
                    editor.execute("copy");
                } else {
                    await copyHtmlToClipboard(html, plainText);
                }
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
            } catch {
                try {
                    await copyHtmlToClipboard(html, plainText);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                } catch {
                    try {
                        if (navigator.clipboard?.writeText) {
                            await navigator.clipboard.writeText(plainText);
                            setCopied(true);
                            window.setTimeout(() => setCopied(false), 2000);
                        }
                    } catch {}
                }
            }
        },
        [hasContent, sampleText]
    );

    const copyButton = (
        <button
            type="button"
            className="floating-panel__action-btn"
            aria-label={copied ? "Sample text copied" : EXEC_SUM_SAMPLE_TEXT_COPY_TIP}
            data-tip={copied ? "Copied" : EXEC_SUM_SAMPLE_TEXT_COPY_TIP}
            data-for={copyTooltipId}
            disabled={!hasContent}
            onClick={handleCopy}
        >
            <i className={`fas ${copied ? "fa-check" : "fa-copy"}`} aria-hidden="true" />
        </button>
    );

    return (
        <>
            <ReactTooltip
                id={copyTooltipId}
                place="top"
                className="rc-tooltip-custom-class floating-panel-tooltip"
                effect="solid"
                backgroundColor="#007bff"
            />
            <FloatingPanel
                title={label || "Band"}
                subtitle="Sample text"
                className="exec-sum-sample-text-viewer-panel"
                initialPosition={initialPosition}
                defaultSize={defaultSize}
                minSize={SAMPLE_TEXT_VIEWER_MIN_SIZE}
                maxSize={SAMPLE_TEXT_VIEWER_MAX_SIZE}
                headerActions={copyButton}
                onClose={onClose}
                zIndex={10050}
                footer={<p className="floating-panel__hint mb-0">Reference only — paste into Content below.</p>}
            >
                {hasContent ? (
                    <div className="exec-sum-sample-text-viewer__editor-wrap">
                        <CKEditor
                            editor={Editor}
                            config={readOnlyEditorConfig}
                            id={`${editorKey}-sample-text-viewer`}
                            data={data || ""}
                            onReady={editor => {
                                if (!editor) return;
                                editorRef.current = editor;
                                editor.setData(data || "");
                                editor.enableReadOnlyMode(SAMPLE_TEXT_VIEWER_READONLY_ID);
                            }}
                            onChange={(event, editor) => {
                                setData(editor.getData());
                            }}
                        />
                    </div>
                ) : (
                    <p className="floating-panel__empty mb-0">No sample text provided for this band.</p>
                )}
            </FloatingPanel>
        </>
    );
}
